import type { EffectsConfig } from "../core/types"
import type React from "react"

/**
 * Tailwind color token → hex map.
 * Covers the most common shades used in gradientText and effects.
 */
export const TAILWIND_COLORS: Record<string, string> = {
  // 400 shades
  "blue-400": "#60a5fa",
  "purple-400": "#c084fc",
  "pink-400": "#f472b6",
  "indigo-400": "#818cf8",
  "cyan-400": "#22d3ee",
  "green-400": "#4ade80",
  "red-400": "#f87171",
  "yellow-400": "#facc15",
  "orange-400": "#fb923c",
  "teal-400": "#2dd4bf",
  "violet-400": "#a78bfa",
  "fuchsia-400": "#e879f9",
  "rose-400": "#fb7185",
  "sky-400": "#38bdf8",
  "emerald-400": "#34d399",
  "amber-400": "#fbbf24",
  "lime-400": "#a3e635",
  "slate-400": "#94a3b8",
  "gray-400": "#9ca3af",
  "zinc-400": "#a1a1aa",
  "neutral-400": "#a3a3a3",
  "stone-400": "#a8a29e",
  // 500 shades
  "blue-500": "#3b82f6",
  "purple-500": "#a855f7",
  "pink-500": "#ec4899",
  "indigo-500": "#6366f1",
  "cyan-500": "#06b6d4",
  "green-500": "#22c55e",
  "red-500": "#ef4444",
  "yellow-500": "#eab308",
  "orange-500": "#f97316",
  "teal-500": "#14b8a6",
  "violet-500": "#8b5cf6",
  "fuchsia-500": "#d946ef",
  "rose-500": "#f43f5e",
  "sky-500": "#0ea5e9",
  "emerald-500": "#10b981",
  "amber-500": "#f59e0b",
  "lime-500": "#84cc16",
  "slate-500": "#64748b",
  "gray-500": "#6b7280",
  "zinc-500": "#71717a",
  "neutral-500": "#737373",
  "stone-500": "#78716c",
  // 600 shades
  "blue-600": "#2563eb",
  "purple-600": "#9333ea",
  "pink-600": "#db2777",
  "indigo-600": "#4f46e5",
  "cyan-600": "#0891b2",
  "green-600": "#16a34a",
  "red-600": "#dc2626",
  "yellow-600": "#ca8a04",
  "orange-600": "#ea580c",
  "teal-600": "#0d9488",
  "violet-600": "#7c3aed",
  "fuchsia-600": "#c026d3",
  "rose-600": "#e11d48",
  "sky-600": "#0284c7",
  "emerald-600": "#059669",
  "amber-600": "#d97706",
  "lime-600": "#65a30d",
  "slate-600": "#475569",
  "gray-600": "#4b5563",
  "zinc-600": "#52525b",
  "neutral-600": "#525252",
  "stone-600": "#57534e",
  // Named colors
  white: "#ffffff",
  black: "#000000",
}

/** Direction token → CSS linear-gradient direction */
export const DIRECTION_MAP: Record<string, string> = {
  "to-r": "to right",
  "to-l": "to left",
  "to-t": "to top",
  "to-b": "to bottom",
  "to-tr": "to top right",
  "to-tl": "to top left",
  "to-br": "to bottom right",
  "to-bl": "to bottom left",
}

/**
 * Resolve a Tailwind color token to a hex value.
 * Falls through to the raw string if not found in the map (e.g. already a hex).
 */
function resolveColor(token: string): string {
  return TAILWIND_COLORS[token] ?? token
}

/**
 * Parse a "color/opacity" string (e.g. "white/10") into an rgba() value.
 * If no slash, returns the resolved hex color.
 */
function parseColorOpacity(raw: string): string {
  const slashIdx = raw.lastIndexOf("/")
  if (slashIdx === -1) return resolveColor(raw)

  const colorPart = raw.slice(0, slashIdx)
  const opacityPart = raw.slice(slashIdx + 1)
  const hex = resolveColor(colorPart)
  const opacity = parseInt(opacityPart, 10) / 100

  // Convert hex to rgb components
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)

  return `rgba(${r},${g},${b},${opacity})`
}

/**
 * Resolves an EffectsConfig to Tailwind CSS utility classes.
 * Handles: backdropBlur, blur, backgroundOpacity.
 */
export function resolveEffectsClasses(effects: EffectsConfig | undefined): string {
  if (!effects) return ""
  const classes: string[] = []

  if (effects.backdropBlur) {
    classes.push(`backdrop-blur-${effects.backdropBlur}`)
  }

  if (effects.blur) {
    classes.push(`blur-${effects.blur}`)
  }

  if (effects.backgroundOpacity != null) {
    const pct = Math.round(effects.backgroundOpacity * 100)
    classes.push(`bg-background/${pct}`)
  }

  if (effects.mixBlendMode) {
    classes.push(`mix-blend-${effects.mixBlendMode}`)
  }

  if (effects.backdropSaturate != null) {
    classes.push(`backdrop-saturate-${Math.round(effects.backdropSaturate * 100)}`)
  }

  if (effects.backdropBrightness != null) {
    classes.push(`backdrop-brightness-${Math.round(effects.backdropBrightness * 100)}`)
  }

  if (effects.backdropContrast != null) {
    classes.push(`backdrop-contrast-${Math.round(effects.backdropContrast * 100)}`)
  }

  return classes.join(" ")
}

/**
 * Resolves an EffectsConfig to React CSSProperties.
 * Handles: gradientText, insetShadow.
 */
export function resolveEffectsStyle(effects: EffectsConfig | undefined): React.CSSProperties {
  if (!effects) return {}
  const style: React.CSSProperties = {}

  if (effects.gradientText) {
    const { from, via, to, direction } = effects.gradientText
    const dir = direction ? (DIRECTION_MAP[direction] ?? direction) : "to right"
    const fromHex = resolveColor(from)
    const toHex = resolveColor(to)

    const stops = via
      ? `${fromHex}, ${resolveColor(via)}, ${toHex}`
      : `${fromHex}, ${toHex}`

    style.backgroundImage = `linear-gradient(${dir}, ${stops})`
    style.WebkitBackgroundClip = "text"
    style.WebkitTextFillColor = "transparent"
    style.backgroundClip = "text"
  }

  if (effects.insetShadow) {
    const { color, blur, spread } = effects.insetShadow
    const rgba = parseColorOpacity(color)
    style.boxShadow = `inset 0 0 ${blur}px ${spread}px ${rgba}`
  }

  if (effects.filter) {
    style.filter = effects.filter
  }

  if (effects.clipPath) {
    style.clipPath = effects.clipPath
  }

  if (effects.maskImage) {
    style.maskImage = effects.maskImage
    ;(style as Record<string, unknown>).WebkitMaskImage = effects.maskImage
  }

  return style
}
